/*!
 * AudoChat - Database Utility
 * Uses localStorage for now. PostgreSQL integration requires backend API.
 */

import { hashPasswordSync, comparePasswordSync } from './password';

// Database keys
const DB_KEYS = {
  USERS: "audochat_users",
  CONVERSATIONS: "audochat_conversations",
  MESSAGES: "audochat_messages",
  CURRENT_USER: "audochat_current_user",
  SETTINGS: "audochat_settings",
};

// User roles
export const ROLES = {
  AGENT: "agent", // Can access Chat, Dashboard, CRM
  ADMIN: "admin", // Can access everything including Settings
};

// Permissions
export const PERMISSIONS = {
  CHAT: "chat",
  DASHBOARD: "dashboard",
  CRM: "crm",
  ITEMS: "items",
  BILLING: "billing",
  SETTINGS: "settings",
  USER_MANAGEMENT: "user_management",
};

// Role permissions mapping
export const ROLE_PERMISSIONS = {
  [ROLES.AGENT]: [
    PERMISSIONS.CHAT,
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.CRM,
    PERMISSIONS.ITEMS,
    PERMISSIONS.BILLING,
  ],
  [ROLES.ADMIN]: [
    PERMISSIONS.CHAT,
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.CRM,
    PERMISSIONS.ITEMS,
    PERMISSIONS.BILLING,
    PERMISSIONS.SETTINGS,
    PERMISSIONS.USER_MANAGEMENT,
  ],
};

// Database utility functions
export const db = {
  // Initialize database with default admin user
  init() {
    const users = this.getUsers();
    const safeUsers = Array.isArray(users) ? users : [];
    if (safeUsers.length === 0) {
      // Create default admin user with hashed password
      const defaultAdmin = {
        id: 1,
        name: "Admin",
        email: "admin@audochat.com",
        password: hashPasswordSync("admin123"), // Hashed password
        role: ROLES.ADMIN,
        createdAt: new Date().toISOString(),
        isActive: true,
      };
      this.saveUsers([defaultAdmin]);
    }
  },

  // Users management
  getUsers() {
    try {
      const users = localStorage.getItem(DB_KEYS.USERS);
      return users ? JSON.parse(users) : [];
    } catch (error) {
      console.error("Error getting users:", error);
      return [];
    }
  },

  saveUsers(users) {
    try {
      localStorage.setItem(DB_KEYS.USERS, JSON.stringify(users));
      return true;
    } catch (error) {
      console.error("Error saving users:", error);
      return false;
    }
  },

  getUserById(id) {
    const users = this.getUsers();
    const safeUsers = Array.isArray(users) ? users : [];
    return safeUsers.find((u) => u && u.id === parseInt(id)) || null;
  },

  getUserByEmail(email) {
    const users = this.getUsers();
    const safeUsers = Array.isArray(users) ? users : [];
    return safeUsers.find((u) => u && u.email === email) || null;
  },

  addUser(user) {
    const users = this.getUsers();
    const safeUsers = Array.isArray(users) ? users : [];
    const newId = safeUsers.length > 0 
      ? Math.max(...safeUsers.map((u) => u && u.id ? u.id : 0)) + 1 
      : 1;
    
    // Hash password if provided
    const hashedPassword = user.password ? hashPasswordSync(user.password) : '';
    
    const newUser = {
      ...user,
      id: newId,
      password: hashedPassword,
      createdAt: new Date().toISOString(),
      isActive: true,
    };
    safeUsers.push(newUser);
    this.saveUsers(safeUsers);
    return newUser;
  },

  updateUser(id, updates) {
    const users = this.getUsers();
    const safeUsers = Array.isArray(users) ? users : [];
    const index = safeUsers.findIndex((u) => u && u.id === parseInt(id));
    if (index !== -1) {
      // Hash password if provided in updates
      const updatedData = { ...updates };
      if (updatedData.password) {
        updatedData.password = hashPasswordSync(updatedData.password);
      }
      safeUsers[index] = { ...safeUsers[index], ...updatedData };
      this.saveUsers(safeUsers);
      return safeUsers[index];
    }
    return null;
  },

  deleteUser(id) {
    const users = this.getUsers();
    const safeUsers = Array.isArray(users) ? users : [];
    const filtered = safeUsers.filter((u) => u && u.id !== parseInt(id));
    this.saveUsers(filtered);
    return true;
  },

  // Authentication
  login(email, password) {
    const user = this.getUserByEmail(email);
    if (user && user.isActive) {
      // Check if password is hashed (starts with $2a$ or $2b$) or plain text (for migration)
      const isHashed = user.password && (user.password.startsWith('$2a$') || user.password.startsWith('$2b$'));
      
      let passwordMatch = false;
      if (isHashed) {
        // Compare with hashed password
        passwordMatch = comparePasswordSync(password, user.password);
      } else {
        // Legacy: compare plain text (for old users)
        passwordMatch = user.password === password;
        // Auto-upgrade: hash the password if it's plain text
        if (passwordMatch) {
          const hashedPassword = hashPasswordSync(password);
          this.updateUser(user.id, { password: hashedPassword });
        }
      }
      
      if (passwordMatch) {
        this.setCurrentUser(user);
        return { success: true, user };
      }
    }
    return { success: false, error: "Invalid email or password" };
  },

  logout() {
    localStorage.removeItem(DB_KEYS.CURRENT_USER);
    return true;
  },

  getCurrentUser() {
    try {
      const user = localStorage.getItem(DB_KEYS.CURRENT_USER);
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  },

  setCurrentUser(user) {
    try {
      localStorage.setItem(DB_KEYS.CURRENT_USER, JSON.stringify(user));
      return true;
    } catch (error) {
      console.error("Error setting current user:", error);
      return false;
    }
  },

  // Check if user has permission
  hasPermission(user, permission) {
    if (!user || !user.role) return false;
    const permissions = ROLE_PERMISSIONS[user.role] || [];
    return permissions.includes(permission);
  },

  // Conversations management
  getConversations() {
    try {
      const conversations = localStorage.getItem(DB_KEYS.CONVERSATIONS);
      return conversations ? JSON.parse(conversations) : [];
    } catch (error) {
      console.error("Error getting conversations:", error);
      return [];
    }
  },

  saveConversations(conversations) {
    try {
      localStorage.setItem(DB_KEYS.CONVERSATIONS, JSON.stringify(conversations));
      return true;
    } catch (error) {
      console.error("Error saving conversations:", error);
      return false;
    }
  },

  getConversationById(id) {
    const conversations = this.getConversations();
    const safeConversations = Array.isArray(conversations) ? conversations : [];
    return safeConversations.find((c) => c && c.id === parseInt(id)) || null;
  },

  addConversation(conversation) {
    const conversations = this.getConversations();
    const safeConversations = Array.isArray(conversations) ? conversations : [];
    const newId = safeConversations.length > 0 
      ? Math.max(...safeConversations.map((c) => c && c.id ? c.id : 0)) + 1 
      : 1;
    const newConversation = {
      ...conversation,
      id: newId,
      createdAt: new Date().toISOString(),
      messages: Array.isArray(conversation.messages) ? conversation.messages : [],
    };
    safeConversations.push(newConversation);
    this.saveConversations(safeConversations);
    return newConversation;
  },

  updateConversation(id, updates) {
    const conversations = this.getConversations();
    const safeConversations = Array.isArray(conversations) ? conversations : [];
    const index = safeConversations.findIndex((c) => c && c.id === parseInt(id));
    if (index !== -1) {
      safeConversations[index] = { ...safeConversations[index], ...updates };
      this.saveConversations(safeConversations);
      return safeConversations[index];
    }
    return null;
  },

  addMessageToConversation(conversationId, message) {
    const conversation = this.getConversationById(conversationId);
    if (conversation) {
      // Ensure messages array exists
      if (!conversation.messages || !Array.isArray(conversation.messages)) {
        conversation.messages = [];
      }
      
      const newMessage = {
        ...message,
        id: conversation.messages.length > 0 ? Math.max(...conversation.messages.map((m) => m && m.id ? m.id : 0)) + 1 : 1,
        timestamp: new Date().toISOString(),
      };
      conversation.messages.push(newMessage);
      conversation.lastMessage = message.text;
      conversation.timestamp = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
      this.updateConversation(conversationId, conversation);
      return newMessage;
    }
    return null;
  },

  // Settings management
  getSettings() {
    try {
      const settings = localStorage.getItem(DB_KEYS.SETTINGS);
      return settings ? JSON.parse(settings) : { accessToken: "", businessAccountId: "" };
    } catch (error) {
      console.error("Error getting settings:", error);
      return { accessToken: "", businessAccountId: "" };
    }
  },

  saveSettings(settings) {
    try {
      localStorage.setItem(DB_KEYS.SETTINGS, JSON.stringify(settings));
      return true;
    } catch (error) {
      console.error("Error saving settings:", error);
      return false;
    }
  },
};

// Initialize database on import
db.init();

export default db;

